// Swipe (/(tabs)/swipe) — web-port van app/(tabs)/swipe.tsx.
// Behoudt de originele Ghost Driver-events: window 'demo:swipe' → 'demo:swipe:animate'.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Icon from '../../Icon';
import { C } from '../../theme';
import { PROPS, DECKS } from '../data';

const sh = { card: '0 6px 16px rgba(15,27,45,0.08)', soft: '0 2px 8px rgba(15,27,45,0.05)' };
const CARD_W = 366; // min(390 - 24, 380)
const CARD_H = 424; // min(max(844 - 420, 380), 520)

function BellButton() {
  return (
    <div style={{
      width: 38, height: 38, borderRadius: 19, background: C.white,
      border: `1px solid ${C.line}`, display: 'flex', alignItems: 'center', justifyContent: 'center',
      position: 'relative', boxShadow: sh.soft,
    }}>
      <Icon name="bell" size={16} color={C.navy} />
      <span style={{ position: 'absolute', top: 8, right: 9, width: 7, height: 7, borderRadius: 4, background: C.gold, border: `1.5px solid ${C.white}` }} />
    </div>
  );
}

function SwipeCard({ p, isCouple, fly }) {
  // Foto-dots, gouden prijspil, Wooncoach-redenen en RouteHint zoals in het origineel.
  const reasons = (p.match_reasons || [])
    .filter((r) => !/binnen (jouw|jullie) budget/i.test(r));
  const withEpc = p.epc && !reasons.some((r) => /epc/i.test(r)) ? [...reasons, `EPC ${p.epc}`] : reasons;
  const shown = withEpc.slice(0, 3);

  const flyStyle = !fly ? {} : fly.dir === 2
    ? { transform: 'translateY(-860px)', transition: 'transform .46s cubic-bezier(.3,.6,.4,1)' }
    : {
        transform: `translateX(${fly.dir * 480}px) rotate(${fly.dir * 10}deg) scale(.5)`,
        transition: 'transform .42s cubic-bezier(.3,.6,.4,1)',
      };

  return (
    <div style={{ width: CARD_W, height: CARD_H, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {/* Groot gouden hart / donker kruis ACHTER de kaart */}
      {fly && fly.dir === 1 ? (
        <div className="gd-shape-pop" style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
          <Icon name="heart" size={220} color={C.gold} strokeWidth={1.6} />
        </div>
      ) : null}
      {fly && fly.dir === -1 ? (
        <div className="gd-shape-pop" style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
          <Icon name="x" size={240} color="rgba(10,26,44,0.85)" strokeWidth={1.6} />
        </div>
      ) : null}

      <div style={{
        position: 'absolute', width: CARD_W, height: CARD_H, borderRadius: 28, overflow: 'hidden',
        background: C.navy, border: `2px solid ${fly ? (fly.dir === 1 ? 'rgba(196,138,58,0.90)' : fly.dir === -1 ? 'rgba(10,26,44,0.85)' : 'rgba(196,138,58,0.6)') : 'transparent'}`,
        boxShadow: sh.card, ...flyStyle,
      }} data-testid="swipe-card">
        <img src={p.img} alt={p.title} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(10,26,44,0) 0%, rgba(10,26,44,0.15) 45%, rgba(10,26,44,0.55) 70%, rgba(10,26,44,0.92) 100%)' }} />

        {/* Foto-dots bovenaan */}
        <div style={{ position: 'absolute', top: 14, left: 14, right: 14, display: 'flex', gap: 4, height: 3 }}>
          {[0, 1, 2].map((i) => (
            <span key={i} style={{ height: 3, flex: 1, background: C.white, borderRadius: 2, opacity: i === 0 ? 1 : 0.35 }} />
          ))}
        </div>

        {/* "Bekijk volledig" — top-left gouden pil */}
        <div style={{
          position: 'absolute', top: 26, left: 16, display: 'flex', alignItems: 'center', gap: 5,
          background: 'rgba(255,255,255,0.94)', padding: '6px 10px', borderRadius: 999,
          border: '1px solid rgba(196,138,58,0.55)', boxShadow: sh.soft,
        }}>
          <Icon name="maximize-2" size={11} color={C.navy} />
          <span style={{ color: C.navy, fontSize: 11.5, fontWeight: 800, letterSpacing: 0.2 }}>Bekijk volledig</span>
        </div>

        {/* Actor-chip (koppel): Tom/Emma swipet */}
        {fly && fly.actor ? (
          <div className="anim-fade-down" style={{
            position: 'absolute', top: 26, right: 16, background: 'rgba(10,26,44,0.85)',
            padding: '5px 11px', borderRadius: 999, border: '1px solid rgba(196,138,58,0.5)',
          }}>
            <span style={{ color: C.white, fontSize: 10.5, fontWeight: 800 }}>{fly.actor === 'tom' ? 'Tom swipet' : 'Emma swipet'}</span>
          </div>
        ) : null}

        {/* Warme gouden gloed / grijze fade na commit */}
        {fly ? (
          <div className="gd-glow-flash" style={{
            position: 'absolute', inset: 0, pointerEvents: 'none',
            background: fly.dir === -1 ? 'rgba(200,204,210,0.28)' : 'rgba(196,138,58,0.22)',
          }} />
        ) : null}

        {/* Onderaan: prijs + stad + Wooncoach-redenen + RouteHint */}
        <div style={{ position: 'absolute', left: 16, right: 16, bottom: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div>
            <span style={{ background: C.gold, padding: '6px 12px', borderRadius: 999, color: C.navy, fontSize: 15, fontWeight: 800, letterSpacing: -0.2, boxShadow: sh.soft }}>
              {p.price}
            </span>
          </div>
          <div style={{ color: 'rgba(255,255,255,0.92)', fontSize: 13, fontWeight: 600 }}>{p.city} · {p.region}</div>

          {shown.length > 0 ? (
            <div style={{ marginTop: 2, paddingTop: 10, borderTop: '1px solid rgba(255,255,255,0.14)', display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 16, height: 16, borderRadius: 8, background: C.gold, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon name="compass" size={10} color={C.navy} />
                </span>
                <span style={{ color: C.gold, fontSize: 9.5, letterSpacing: 1.4, fontWeight: 800 }}>WOONCOACH</span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                {shown.map((r) => (
                  <span key={r} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                    background: 'rgba(255,255,255,0.14)', padding: '5px 8px', borderRadius: 999,
                  }}>
                    <span style={{ width: 14, height: 14, borderRadius: 7, background: C.gold, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Icon name={/epc/i.test(r) ? 'zap' : 'check'} size={9} color={C.navy} />
                    </span>
                    <span style={{ color: C.white, fontSize: 11, fontWeight: 700, letterSpacing: 0.1 }}>{r}</span>
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          {/* RouteHint — demo zonder plaatsen: subtiele setup-CTA */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6, marginTop: 2,
            padding: '7px 10px', background: 'rgba(196,138,58,0.14)',
            border: '1px solid rgba(196,138,58,0.30)', borderRadius: 14,
          }}>
            <Icon name="map-pin" size={12} color={C.gold} />
            <span style={{ flex: 1, color: C.gold, fontSize: 10.5, fontWeight: 700, letterSpacing: 0.1 }}>
              Voeg {isCouple ? 'jullie' : 'jouw'} dagelijkse plaatsen toe om afstanden (km) te zien
            </span>
            <Icon name="chevron-right" size={12} color={C.gold} />
          </div>
        </div>
      </div>
    </div>
  );
}

function StaticCard({ p }) {
  return (
    <div style={{
      position: 'absolute', width: CARD_W, height: CARD_H, borderRadius: 28, overflow: 'hidden',
      background: C.navy, transform: 'scale(0.94) translateY(14px)', opacity: 0.55,
    }}>
      <img src={p.img} alt={p.title} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(10,26,44,0.15), rgba(10,26,44,0.55))' }} />
    </div>
  );
}

function EmptyDeck({ onReset }) {
  return (
    <div style={{ padding: '0 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
      <div style={{ width: 62, height: 62, borderRadius: 31, background: C.white, border: `1px solid ${C.line}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Icon name="check-circle" size={30} color={C.gold} />
      </div>
      <div style={{ color: C.navy, fontSize: 24, fontWeight: 700, letterSpacing: -0.3 }}>Alles bekeken</div>
      <div style={{ color: C.inkMuted, fontSize: 15, textAlign: 'center' }}>Pas je voorkeuren aan of ontdek meer inspiratie op de Ontdek-tab.</div>
      <button className="immo-btn" data-testid="reset-deck" onClick={onReset} style={{ marginTop: 8, padding: '12px 20px', borderRadius: 999, background: C.navy }}>
        <span style={{ color: C.white, fontSize: 14, fontWeight: 600 }}>Opnieuw laden</span>
      </button>
    </div>
  );
}

function ActionPill({ icon, label, onClick, disabled, primary, subtle, testid }) {
  return (
    <button
      className="immo-btn"
      data-testid={testid}
      onClick={onClick}
      disabled={disabled}
      style={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
        background: primary ? C.navy : subtle ? 'rgba(255,255,255,0.7)' : C.white,
        border: `1px solid ${primary ? C.navy : C.line}`,
        padding: '11px 10px', borderRadius: 999, boxShadow: subtle ? 'none' : sh.soft,
        opacity: disabled ? 0.4 : 1,
      }}
    >
      <Icon name={icon} size={14} color={primary ? C.white : subtle ? C.inkFaint : C.navy} />
      <span style={{ color: primary ? C.white : subtle ? C.inkFaint : C.navy, fontSize: 13, fontWeight: subtle ? 600 : 700 }}>{label}</span>
    </button>
  );
}

export default function SwipeApp({ mode }) {
  const isCouple = mode === 'couple';
  const items = (DECKS[mode] || DECKS.solo).map((k) => PROPS[k]);
  const [idx, setIdx] = useState(0);
  const [history, setHistory] = useState([]);
  const [fly, setFly] = useState(null); // { dir: 1|-1|2, actor }
  const flyRef = useRef(null);
  flyRef.current = fly;

  const commit = useCallback((decision, actor) => {
    const dir = decision === 'yes' ? 1 : decision === 'no' ? -1 : 2;
    setFly({ dir, actor: actor || null, key: Date.now() });
    // Na de animatie: registreer beslissing (zoals in het origineel, 500ms)
    setTimeout(() => {
      setHistory((h) => [...h, decision]);
      setIdx((i) => Math.min(i + 1, items.length));
      setFly(null);
    }, 500);
  }, [items.length]);

  // ─── Demo Ghost Driver · programmatische swipe ────────────────────────
  // Luistert naar `demo:swipe` events (Web) en animeert de bovenste kaart.
  useEffect(() => {
    const handler = (e) => {
      const raw = e?.detail?.decision;
      const decision = raw === 'no' ? 'no' : raw === 'maybe' ? 'maybe' : 'yes';
      const actor = e?.detail?.actor || null;
      // Klein visueel signaal: driver animeert de bovenste kaart uit beeld
      try {
        window.dispatchEvent(new CustomEvent('demo:swipe:animate', {
          detail: { direction: decision === 'yes' ? 1 : decision === 'no' ? -1 : 2, actor },
        }));
      } catch {}
      setTimeout(() => commit(decision, actor), 0);
    };
    window.addEventListener('demo:swipe', handler);
    return () => window.removeEventListener('demo:swipe', handler);
  }, [commit]);

  const undo = useCallback(() => {
    setHistory((h) => {
      if (!h.length) return h;
      setIdx((i) => Math.max(0, i - 1));
      return h.slice(0, -1);
    });
  }, []);

  const current = items[idx];
  const next = items[idx + 1];

  return (
    <div data-testid="swipe-screen" style={{ minHeight: '100%', background: C.bgSoft, position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, #F1EBDF, #EEF3F9, #F1EBDF)', pointerEvents: 'none' }} />

      {/* Header — WOONMATCH + FILTER / MELDINGEN / COACH */}
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '46px 24px 4px' }}>
        <div style={{ flex: 1 }}>
          <div style={{ color: C.gold, fontSize: 11, letterSpacing: 2, fontWeight: 800, marginBottom: 2 }}>WOONMATCH</div>
          <div style={{ color: C.navy, fontSize: 20, fontWeight: 600, letterSpacing: -0.2 }}>
            {isCouple ? 'Match jullie droomwoning' : 'Match jouw droomwoning'}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div style={{ width: 38, height: 38, borderRadius: 19, background: C.white, border: `1px solid ${C.line}`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: sh.soft }} data-testid="filter-button">
            <Icon name="sliders" size={17} color={C.navy} />
          </div>
          <span style={{ color: C.inkFaint, marginTop: 3, fontSize: 8.5, letterSpacing: 1.1, fontWeight: 700 }}>FILTER</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginLeft: 6 }}>
          <BellButton />
          <span style={{ color: C.inkFaint, marginTop: 3, fontSize: 8.5, letterSpacing: 1.1, fontWeight: 700 }}>MELDINGEN</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginLeft: 6 }}>
          <div style={{ width: 38, height: 38, borderRadius: 19, background: 'rgba(196,138,58,0.14)', border: '1px solid rgba(196,138,58,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} data-testid="coach-button">
            <Icon name="compass" size={17} color={C.gold} />
          </div>
          <span style={{ color: C.inkFaint, marginTop: 3, fontSize: 8.5, letterSpacing: 1.1, fontWeight: 700 }}>COACH</span>
        </div>
      </div>

      {/* Deck */}
      <div style={{ position: 'relative', flex: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: 10, paddingBottom: 8, minHeight: CARD_H + 20 }}>
        {!current ? (
          <EmptyDeck onReset={() => { setIdx(0); setHistory([]); }} />
        ) : (
          <div style={{ position: 'relative', width: CARD_W, height: CARD_H }}>
            {next ? <StaticCard p={next} /> : null}
            <SwipeCard key={`${idx}-${fly ? fly.key : 'idle'}`} p={current} isCouple={isCouple} fly={fly} />
          </div>
        )}
      </div>

      {/* Onderbalk — Terug · Misschien · Bewaren */}
      {current ? (
        <div style={{ position: 'relative', display: 'flex', justifyContent: 'center', gap: 8, padding: '12px 12px 16px' }} data-testid="swipe-actions">
          <ActionPill icon="rotate-ccw" label="Terug" onClick={undo} disabled={history.length === 0} subtle testid="btn-undo" />
          <ActionPill icon="bookmark" label="Misschien" onClick={() => commit('maybe')} testid="btn-maybe" />
          <ActionPill icon="heart" label="Bewaren" onClick={() => commit('yes')} primary testid="btn-yes" />
        </div>
      ) : null}
    </div>
  );
}
